import type { OrchestrationGroup, ToolGroup } from "../events/messageGroups"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { harnessIdForProvider, isHarnessId } from "../../modelRegistry"
import { TASK_TOOL_NAMES } from "../../product"

const MAX_EMBEDDED_RESULT_CHARS = 200_000
const MAX_SEARCH_DEPTH = 8
const MAX_SESSION_RECORDS = 8

export function taskSessionFromLaunchGroup(group: ToolGroup, taskSessions: readonly TaskSessionDebugInfo[] = []): TaskSessionDebugInfo | null {
    return taskSessionsFromLaunchGroup(group, taskSessions)[0] ?? null
}

export function taskSessionsFromLaunchGroup(group: ToolGroup | OrchestrationGroup, taskSessions: readonly TaskSessionDebugInfo[] = []): TaskSessionDebugInfo[] {
    if (!isSuccessfulSessionLaunchGroup(group)) return []

    const input = group.type === "tool" ? recordValue(group.input) : null
    return findSessionRecords(group.result).flatMap((sessionRecord) => {
        const sessionId = stringValue(sessionRecord.sessionId)
        if (!sessionId?.startsWith("ses_")) return []

        const knownSession = taskSessions.find((session) => session.nestSessionId === sessionId)
        if (knownSession) return [knownSession]

        const providerId = stringValue(sessionRecord.providerId)
        const inputHarnessId = stringValue(input?.harnessId)
        const harnessId = (isHarnessId(inputHarnessId) ? inputHarnessId : harnessIdForProvider(providerId)) ?? "claude-code"

        return [
            {
                key: sessionId,
                harnessId,
                role: stringValue(sessionRecord.role) ?? "auxiliary",
                label: stringValue(sessionRecord.label) ?? stringValue(input?.label),
                parentSessionId: stringValue(sessionRecord.parentSessionId),
                providerId,
                providerSessionId: stringValue(sessionRecord.providerSessionId),
                nestSessionId: sessionId,
                model: stringValue(sessionRecord.model) ?? stringValue(input?.model),
                mode: stringValue(sessionRecord.mode) ?? stringValue(input?.mode),
                cwd: stringValue(sessionRecord.cwd) ?? stringValue(input?.cwd),
                status: stringValue(sessionRecord.status),
                activeTurnId: stringValue(sessionRecord.activeTurnId),
                createdAt: stringValue(sessionRecord.createdAt),
                updatedAt: stringValue(sessionRecord.updatedAt),
            },
        ]
    })
}

function isSuccessfulSessionLaunchGroup(group: ToolGroup | OrchestrationGroup): boolean {
    if (group.isError) return false
    if (group.type === "tool") return group.toolName.toLowerCase().endsWith(TASK_TOOL_NAMES.startSession)
    return group.calls.some((call) => call.toolName.toLowerCase().endsWith(TASK_TOOL_NAMES.startSession))
}

function findSessionRecords(value: unknown): Record<string, unknown>[] {
    const records: Record<string, unknown>[] = []
    const sessionIds = new Set<string>()
    collectSessionRecords(value, records, sessionIds, 0)
    return records
}

function collectSessionRecords(value: unknown, records: Record<string, unknown>[], sessionIds: Set<string>, depth: number): void {
    if (depth > MAX_SEARCH_DEPTH || records.length >= MAX_SESSION_RECORDS) return
    if (typeof value === "string") {
        for (const parsed of parseEmbeddedJson(value)) {
            collectSessionRecords(parsed, records, sessionIds, depth + 1)
            if (records.length >= MAX_SESSION_RECORDS) return
        }
        return
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            collectSessionRecords(item, records, sessionIds, depth + 1)
            if (records.length >= MAX_SESSION_RECORDS) return
        }
        return
    }

    const record = recordValue(value)
    if (!record) return

    const nestedSession = recordValue(record.session)
    if (nestedSession && addSessionRecord(nestedSession, records, sessionIds)) return
    if (addSessionRecord(record, records, sessionIds)) return

    for (const nested of Object.values(record)) {
        collectSessionRecords(nested, records, sessionIds, depth + 1)
        if (records.length >= MAX_SESSION_RECORDS) return
    }
}

function addSessionRecord(record: Record<string, unknown>, records: Record<string, unknown>[], sessionIds: Set<string>): boolean {
    const sessionId = stringValue(record.sessionId)
    if (!sessionId?.startsWith("ses_")) return false
    if (!sessionIds.has(sessionId)) {
        sessionIds.add(sessionId)
        records.push(record)
    }
    return true
}

function* parseEmbeddedJson(value: string): Generator<unknown> {
    if (value.length > MAX_EMBEDDED_RESULT_CHARS) return
    const trimmed = value.trim()
    const outputMarker = "\nOutput:\n"
    const outputIndex = trimmed.lastIndexOf(outputMarker)
    const candidate = outputIndex >= 0 ? trimmed.slice(outputIndex + outputMarker.length).trim() : trimmed
    const parsed = parseJsonValue(candidate)
    if (parsed !== null) {
        yield parsed
        return
    }

    // Separate Codex text(...) results become newline-delimited JSON when
    // native content blocks are flattened. Keep each result independently.
    for (const line of candidate.split("\n")) {
        const parsedLine = parseJsonValue(line.trim())
        if (parsedLine !== null) yield parsedLine
    }
}

function parseJsonValue(candidate: string): unknown | null {
    if (!(candidate.startsWith("{") || candidate.startsWith("["))) return null
    try {
        return JSON.parse(candidate)
    } catch {
        return null
    }
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 ? value : undefined
}
